import React, { useState } from "react";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Modal,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Drawer,
  SwipeableDrawer,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import MenuIcon from "@mui/icons-material/Menu";
import AppsIcon from "@mui/icons-material/Apps";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// 👇 UPDATED: Added getUser import
import { logout, getUser } from "../services/authService";
import logoutImg from '../icon/logout.png';
import whiteLogo from '../icon/white-logo.png';

const DashboardAnalyzer = () => {
  const [collapsed, setCollapsed] = useState(false);
  const loggedInUser = (() => { try { const u = JSON.parse(localStorage.getItem("user")); return u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : u?.firstName || ""; } catch { return ""; } })();
  
  // 👇 ADDED: Fetch the user's allowed models
  const user = getUser();
  const allowedModels = user?.allowedModels || [];

  const [tab, setTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [showLoanOverlay, setShowLoanOverlay] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [currentAppId, setCurrentAppId] = useState(null);
  
  const [loadingMessage, setLoadingMessage] = useState("");

  const [dashboardImageUrl, setDashboardImageUrl] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  const [fraudImageUrl, setFraudImageUrl] = useState(null);
  const [fraudJsonData, setFraudJsonData] = useState(null);
  const [fraudProcessing, setFraudProcessing] = useState(false);
  
  const [probabilityImageUrl, setProbabilityImageUrl] = useState(null);
  const [probabilityJsonData, setProbabilityJsonData] = useState(null);
  const [probabilityProcessing, setProbabilityProcessing] = useState(false);
  
  const [identityImageUrl, setIdentityImageUrl] = useState(null);
  const [identityJsonData, setIdentityJsonData] = useState(null);
  const [identityProcessing, setIdentityProcessing] = useState(false);
  
  const [eligibilityImageUrl, setEligibilityImageUrl] = useState(null);
  const [eligibilityJsonData, setEligibilityJsonData] = useState(null);
  const [eligibilityProcessing, setEligibilityProcessing] = useState(false);
  
  const [prequalImageUrl, setPrequalImageUrl] = useState(null);
  const [prequalJsonData, setPrequalJsonData] = useState(null);
  const [prequalProcessing, setPrequalProcessing] = useState(false);
  
  const [earlyWarningImageUrl, setEarlyWarningImageUrl] = useState(null);
  const [earlyWarningJsonData, setEarlyWarningJsonData] = useState(null);
  const [earlyWarningProcessing, setEarlyWarningProcessing] = useState(false);
  
  const [collectionImageUrl, setCollectionImageUrl] = useState(null);
  const [collectionJsonData, setCollectionJsonData] = useState(null);
  const [collectionProcessing, setCollectionProcessing] = useState(false);
  
  const [bureauImageUrl, setBureauImageUrl] = useState(null);
  const [bureauJsonData, setBureauJsonData] = useState(null);
  const [bureauProcessing, setBureauProcessing] = useState(false);
  
  const [creditmemoImageUrl, setCreditmemoImageUrl] = useState(null);
  const [creditmemoJsonData, setCreditmemoJsonData] = useState(null);
  const [creditmemoProcessing, setCreditmemoProcessing] = useState(false);
  
  const [bizverImageUrl, setBizverImageUrl] = useState(null);
  const [bizverJsonData, setBizverJsonData] = useState(null);
  const [bizverProcessing, setBizverProcessing] = useState(false);
  
  const [uploadFileType, setUploadFileType] = useState("pdf");
  const [activeMode, setActiveMode] = useState(null);
  const [toast, setToast] = useState("");
  const [fileWarning, setFileWarning] = useState("");
  const [uploadMode, setUploadMode] = useState("new");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  const AZURE_BASE = "https://deetsxstorage.blob.core.windows.net/lendingx-sandbox";

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4000);
  };

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const handleUpload = async (e) => {
    setUploadError("");
    const imageExts = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"];
    const allSelected = Array.from(e.target.files);
    const pdfs = allSelected.filter((f) => f.type === "application/pdf");
    const images = allSelected.filter((f) => imageExts.includes(f.type));
    if (pdfs.length === 0 && images.length === 0) { setUploadError("Only PDF or image files are allowed."); return; }
    setUploading(true);
    try {
      const token = localStorage.getItem("authToken");
      let appId = currentAppId;
      let uploadedFiles = [];

      if (pdfs.length > 0) {
        const formData = new FormData();
        pdfs.forEach((f) => formData.append("files", f));
        const res = await fetch("/api/analyzer-upload", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
        const data = await res.json();
        if (!res.ok || !data.success) { setUploadError(data.error || "Upload failed."); return; }
        appId = data.applicationId;
        uploadedFiles = [...uploadedFiles, ...pdfs.map((f) => ({ name: f.name, tag: "PDF", meta: `${(f.size / 1024).toFixed(1)} KB`, checked: true, applicationId: appId }))];
      }

      if (images.length > 0) {
        const formData = new FormData();
        images.forEach((f) => formData.append("files", f));
        if (appId) formData.append("applicationId", appId);
        const res = await fetch("/api/analyzer-upload-images", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
        const data = await res.json();
        if (!res.ok || !data.success) { setUploadError(data.error || "Image upload failed."); return; }
        if (!appId) appId = data.applicationId;
        uploadedFiles = [...uploadedFiles, ...images.map((f) => ({ name: f.name, tag: "IMG", meta: `${(f.size / 1024).toFixed(1)} KB`, checked: true, applicationId: appId }))];
      }

      setFiles(uploadedFiles);
      setCurrentAppId(appId);
      setOpenModal(false);
      showToast("Files uploaded successfully");
    } catch (err) { setUploadError("Upload failed. Please try again."); } finally { setUploading(false); }
  };

  const handleAddToExisting = async (e) => {
    setUploadError("");
    const imageExts = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"];
    const allSelected = Array.from(e.target.files);
    const pdfs = allSelected.filter((f) => f.type === "application/pdf");
    const images = allSelected.filter((f) => imageExts.includes(f.type));
    if (pdfs.length === 0 && images.length === 0) { setUploadError("Only PDF or image files are allowed."); return; }
    setUploading(true);
    try {
      const token = localStorage.getItem("authToken");
      let uploadedFiles = [];

      if (pdfs.length > 0) {
        const formData = new FormData();
        pdfs.forEach((f) => formData.append("files", f));
        formData.append("applicationId", currentAppId);
        const res = await fetch("/api/analyzer-upload-existing", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
        const data = await res.json();
        if (!res.ok || !data.success) { setUploadError(data.error || "Upload failed."); return; }
        uploadedFiles = [...uploadedFiles, ...pdfs.map((f) => ({ name: f.name, tag: "PDF", meta: `${(f.size / 1024).toFixed(1)} KB`, checked: true, applicationId: currentAppId }))];
      }

      if (images.length > 0) {
        const formData = new FormData();
        images.forEach((f) => formData.append("files", f));
        formData.append("applicationId", currentAppId);
        const res = await fetch("/api/analyzer-upload-images", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
        const data = await res.json();
        if (!res.ok || !data.success) { setUploadError(data.error || "Image upload failed."); return; }
        uploadedFiles = [...uploadedFiles, ...images.map((f) => ({ name: f.name, tag: "IMG", meta: `${(f.size / 1024).toFixed(1)} KB`, checked: true, applicationId: currentAppId }))];
      }

      setFiles((prev) => [...prev, ...uploadedFiles]);
      setOpenModal(false);
      showToast("Files added to existing case");
    } catch (err) { setUploadError("Upload failed. Please try again."); } finally { setUploading(false); }
  };

  const toggleCheck = (index) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, checked: !f.checked } : f)));
  };

  const deleteFile = async (index) => {
    const file = files[index];
    if (currentAppId && file) {
      const token = localStorage.getItem("authToken");
      try {
        await fetch("/api/analyzer-delete-file", { method: "DELETE", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, fileName: file.name }) });
        showToast(`${file.name} deleted successfully`);
      } catch (err) { console.error("[DELETE] Failed to delete from Azure:", err); }
    }
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const navigate = useNavigate();
  const handleLogout = () => { logout("manual"); };

  // ── Enterprise Exponential Backoff Polling ──
  const pollUntilReady = async (appId, endpointPrefix, setJson, setImg, setProcessingIndicator, azureImageUrls = []) => {
    const maxWaitMs = 180000; // 3 mins total timeout
    const start = Date.now();
    const token = localStorage.getItem("authToken");
    const headers = { Authorization: `Bearer ${token}` };

    let jsonReady = false;
    let jsonFoundTimestamp = null;
    let currentDelay = 5000; // Start with a 5-second poll to reduce initial 404 noise

    while (Date.now() - start < maxWaitMs) {
      try {
        // 1. Check for JSON
        if (!jsonReady) {
          const jRes = await fetch(`/api/${endpointPrefix}-result/${appId}/json`, { headers });
          if (jRes.ok && jRes.status === 200) {
            const data = await jRes.json();
            if (!data.error && data.success !== false && data.status !== 'processing') {
              jsonReady = true;
              jsonFoundTimestamp = Date.now();
              setJson(data); 
              setLoadingMessage("Step 2: Data extracted! AI drawing dashboard...");
              currentDelay = 4000; // Reset delay for the image check phase
            }
          }
        }

        // 2. Check for Image
        if (jsonReady) {
          let imgFound = false;

          if (azureImageUrls && azureImageUrls.length > 0) {
            for (const url of azureImageUrls) {
              const testUrl = `${url}?t=${Date.now()}`;
              // Silent ping completely bypasses the red 404s in the fetch network log
              const exists = await checkImageExists(testUrl);
              if (exists) {
                setImg(testUrl);
                imgFound = true;
                break;
              }
            }
          } else {
            imgFound = true; 
          }

          if (imgFound) {
            setProcessingIndicator(false);
            setLoadingMessage("");
            return; // FULL SUCCESS
          }

          // Fail-safe: Avoid infinite spinner if Python crashed on the image stage
          if (Date.now() - jsonFoundTimestamp > 60000) {
            console.warn(`[Fail-Safe] Image timeout for ${appId}. Rendering JSON only.`);
            showToast('Dashboard generation timed out. Showing extracted data only.');
            setProcessingIndicator(false);
            setLoadingMessage("");
            return;
          }
        }
      } catch (e) {
        // Silently ignore network failures and keep polling
      }
      
      await new Promise(r => setTimeout(r, currentDelay));
      // Exponential Backoff: Increase delay, capping at 10 seconds.
      currentDelay = Math.min(currentDelay + 2000, 10000);
    }
    
    console.warn(`[Fail-Safe] Polling completely timed out for ${appId}.`);
    showToast('Processing completely timed out. Please try again.');
    setProcessingIndicator(false);
    setLoadingMessage("");
  };

  // ── Handlers ──
  // const handleLoanApproverScore = () => {
  //   if (!currentAppId) return;
  //   const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
  //   if (tickedFiles.length === 0) return;
  //   if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Loan Approval Score."); return; }
    
  //   setTab(0); setActiveMode("loan"); setProcessing(true); setDashboardImageUrl(null); setJsonData(null);
  //   setLoadingMessage("Step 1: Extracting intelligence data...");
  //   const token = localStorage.getItem("authToken");
    
  //   fetch("/api/analyzer-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
  //     .then((r) => {
  //       if (r.ok || r.status === 202) {
  //         pollUntilReady(currentAppId, "analyzer", setJsonData, setDashboardImageUrl, setProcessing, [
  //           `${AZURE_BASE}/${currentAppId}/Output_Image/final_dashboard.png`,
  //           `${AZURE_BASE}/${currentAppId}/Output_Image/final_dashboard.jpg`
  //         ]);
  //       } else {
  //         setProcessing(false); showToast("Failed to start processing.");
  //       }
  //     })
  //     .catch(() => { setProcessing(false); showToast("Error connecting to server."); });
  // };

  const handleLoanApproverScore = (engineType) => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Loan Approval Score."); return; }
    
    // Set UI state. We track which engine is active via the activeMode string
    const modeString = engineType === 'private' ? "loan_private" : "loan_gemini";
    setTab(0); 
    setActiveMode(modeString); 
    setProcessing(true); 
    setDashboardImageUrl(null); 
    setJsonData(null);
    
    // Dynamic loading message
    const engineName = engineType === 'private' ? "Gemma 4 (Local Vault)" : "Gemini (Cloud)";
    setLoadingMessage(`Step 1: Extracting intelligence data via ${engineName}...`);
    
    const token = localStorage.getItem("authToken");
    
    // Send the engine flag to the backend
    fetch("/api/analyzer-process", { 
      method: "POST", 
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, 
      body: JSON.stringify({ applicationId: currentAppId, tickedFiles, engine: engineType }) 
    })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "analyzer", setJsonData, setDashboardImageUrl, setProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/final_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/final_dashboard.jpg`
          ]);
        } else {
          setProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setProcessing(false); showToast("Error connecting to server."); });
  };

  const handleFraudRiskScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Fraud Risk Score."); return; }
    
    setTab(0); setActiveMode("fraud"); setFraudProcessing(true); setFraudImageUrl(null); setFraudJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/fraud-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "fraud", setFraudJsonData, setFraudImageUrl, setFraudProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/fraud_risk_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/fraud_risk_dashboard.jpg`
          ]);
        } else {
          setFraudProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setFraudProcessing(false); showToast("Error connecting to server."); });
  };

  const handleProbabilityScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use New Loan Score."); return; }
    
    setTab(0); setActiveMode("probability"); setProbabilityProcessing(true); setProbabilityImageUrl(null); setProbabilityJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/probability-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "probability", setProbabilityJsonData, setProbabilityImageUrl, setProbabilityProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/new_loan_score.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/new_loan_score.jpg`
          ]);
        } else {
          setProbabilityProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setProbabilityProcessing(false); showToast("Error connecting to server."); });
  };

  const handleIdentityScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Identity Confidence Score."); return; }
    
    setTab(0); setActiveMode("identity"); setIdentityProcessing(true); setIdentityImageUrl(null); setIdentityJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/identity-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "identity", setIdentityJsonData, setIdentityImageUrl, setIdentityProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/identity_confidence_score_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/identity_confidence_score_dashboard.jpg`
          ]);
        } else {
          setIdentityProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setIdentityProcessing(false); showToast("Error connecting to server."); });
  };

  const handleEligibilityScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Loan Product Recommendation."); return; }
    
    setTab(0); setActiveMode("eligibility"); setEligibilityProcessing(true); setEligibilityImageUrl(null); setEligibilityJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/eligibility-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "eligibility", setEligibilityJsonData, setEligibilityImageUrl, setEligibilityProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/loan_eligibility_engine_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/loan_eligibility_engine_dashboard.jpg`
          ]);
        } else {
          setEligibilityProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setEligibilityProcessing(false); showToast("Error connecting to server."); });
  };

  const handlePrequalScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Pre-Qualification Score."); return; }
    
    setTab(0); setActiveMode("prequal"); setPrequalProcessing(true); setPrequalImageUrl(null); setPrequalJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/prequalification-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "prequalification", setPrequalJsonData, setPrequalImageUrl, setPrequalProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/pre_qualification_score_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/pre_qualification_score_dashboard.jpg`
          ]);
        } else {
          setPrequalProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setPrequalProcessing(false); showToast("Error connecting to server."); });
  };

  const handleEarlyWarningScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Early Warning Score."); return; }
    
    setTab(0); setActiveMode("earlywarning"); setEarlyWarningProcessing(true); setEarlyWarningImageUrl(null); setEarlyWarningJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/earlywarning-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "earlywarning", setEarlyWarningJsonData, setEarlyWarningImageUrl, setEarlyWarningProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/early_warning_score_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/early_warning_score_dashboard.jpg`
          ]);
        } else {
          setEarlyWarningProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setEarlyWarningProcessing(false); showToast("Error connecting to server."); });
  };

  const handleCollectionScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Collection Score."); return; }
    
    setTab(0); setActiveMode("collection"); setCollectionProcessing(true); setCollectionImageUrl(null); setCollectionJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/collection-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "collection", setCollectionJsonData, setCollectionImageUrl, setCollectionProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/collection_score_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/collection_score_dashboard.jpg`
          ]);
        } else {
          setCollectionProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setCollectionProcessing(false); showToast("Error connecting to server."); });
  };

  const handleBureauScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Unified Bureau Score."); return; }
    
    setTab(0); setActiveMode("bureau"); setBureauProcessing(true); setBureauImageUrl(null); setBureauJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/bureau-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "bureau", setBureauJsonData, setBureauImageUrl, setBureauProcessing, [
            `${AZURE_BASE}/${currentAppId}/Output_Image/Unified_Bureau_Report_dashboard.png`,
            `${AZURE_BASE}/${currentAppId}/Output_Image/Unified_Bureau_Report_dashboard.jpg`
          ]);
        } else {
          setBureauProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setBureauProcessing(false); showToast("Error connecting to server."); });
  };

  const handleCreditmemoScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;
    if (!hasPdfSelected()) { setFileWarning("Please upload PDF files to use Credit Memo Score."); return; }
    
    setTab(0); setActiveMode("creditmemo"); setCreditmemoProcessing(true); setCreditmemoImageUrl(null); setCreditmemoJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    fetch("/api/creditmemo-process", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "creditmemo", setCreditmemoJsonData, setCreditmemoImageUrl, setCreditmemoProcessing, [
            `${AZURE_BASE}/${currentAppId}/Original_Docs/credit_memo.png`,
            `${AZURE_BASE}/${currentAppId}/Original_Docs/credit_memo.jpg`
          ]);
        } else {
          setCreditmemoProcessing(false); showToast("Failed to start processing.");
        }
      })
      .catch(() => { setCreditmemoProcessing(false); showToast("Error connecting to server."); });
  };

  // ---------------------------------------------------------
  // FIXED FUNCTION: Image validation + Polling implemented here
  // ---------------------------------------------------------
  const handleBizverScore = () => {
    if (!currentAppId) return;
    const tickedFiles = files.filter((f) => f.checked).map((f) => f.name);
    if (tickedFiles.length === 0) return;

    // 1. Validate that an Image is selected
    const hasImageSelected = files.some((f) => f.checked && f.tag === "IMG");
    if (!hasImageSelected) {
      setFileWarning("Please upload Image files to use Business Site Verification.");
      return;
    }

    // 2. Set UI State
    setTab(0); setActiveMode("bizver"); setBizverProcessing(true); setBizverImageUrl(null); setBizverJsonData(null);
    setLoadingMessage("Step 1: Extracting intelligence data...");
    const token = localStorage.getItem("authToken");
    
    // 3. Fire API and Poll (Fixes the instant 404s)
    fetch("/api/businessverification-process", { 
      method: "POST", 
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, 
      body: JSON.stringify({ applicationId: currentAppId, tickedFiles }) 
    })
      .then((r) => {
        if (r.ok || r.status === 202) {
          pollUntilReady(currentAppId, "businessverification", setBizverJsonData, setBizverImageUrl, setBizverProcessing, [
            `${AZURE_BASE}/${currentAppId}/Original_Docs/business_site_verification.png`,
            `${AZURE_BASE}/${currentAppId}/Original_Docs/business_site_verification.jpg`
          ]);
        } else {
          setBizverProcessing(false); 
          showToast("Failed to start processing.");
        }
      })
      .catch(() => { 
        setBizverProcessing(false); 
        showToast("Error connecting to server."); 
      });
  };

  const handleUploadImages = async (e) => {
    setUploadError("");
    const imageExts = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"];
    const selected = Array.from(e.target.files).filter((f) => imageExts.includes(f.type));
    if (selected.length === 0) { setUploadError("Only image files are allowed (jpg, png, webp, etc.)."); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      selected.forEach((f) => formData.append("files", f));
      if (currentAppId) formData.append("applicationId", currentAppId);
      const token = localStorage.getItem("authToken");
      const res = await fetch("/api/analyzer-upload-images", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (!res.ok || !data.success) { setUploadError(data.error || "Upload failed."); return; }
      const uploaded = selected.map((f) => ({ name: f.name, tag: "IMG", meta: `${(f.size / 1024).toFixed(1)} KB`, checked: true, applicationId: data.applicationId }));
      if (!currentAppId) { setFiles(uploaded); setCurrentAppId(data.applicationId); } else { setFiles((prev) => [...prev, ...uploaded]); }
      setOpenModal(false);
      showToast("Images uploaded successfully");
    } catch (err) { setUploadError("Upload failed. Please try again."); } finally { setUploading(false); }
  };

  const isAnyProcessing = processing || fraudProcessing || probabilityProcessing || identityProcessing || eligibilityProcessing || prequalProcessing || earlyWarningProcessing || collectionProcessing || bureauProcessing || creditmemoProcessing || bizverProcessing;

  const hasPdfSelected = () => files.filter((f) => f.checked && f.tag === "PDF").length > 0;

  const displayFiles = files;

  // 👇 UPDATED: Added featureCode mapping to the hardcoded services array
  const services = [
    // { label: "Loan Approval Score",        icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   onClick: handleLoanApproverScore,  active: processing,              color: "#00A88F", featureCode: "MODEL_LOAN_APPROVAL" },
    { 
      label: "Loan Approval Score (Cloud)",        
      icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   
      onClick: () => handleLoanApproverScore('gemini'),  
      active: processing && activeMode === "loan_gemini",              
      color: "#00A88F", 
      featureCode: "MODEL_LOAN_APPROVAL_GEMINI" 
    },
    { 
      label: "Loan Approval Score (Private)",        
      icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   
      onClick: () => handleLoanApproverScore('private'),  
      active: processing && activeMode === "loan_private",              
      color: "#00c4a8", 
      featureCode: "MODEL_LOAN_APPROVAL_PRIVATE" 
    },
    { label: "Identity Confidence Score",  icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   onClick: handleIdentityScore,      active: identityProcessing,       color: "#b478ff", featureCode: "MODEL_IDENTITY_CONFIDENCE" },
    { label: "Credit Memo Report",          icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   onClick: handleCreditmemoScore,    active: creditmemoProcessing,     color: "#ffc864", featureCode: "MODEL_CREDIT_MEMO" },
    {
      label: "Business Site Verification",
      icon: <StarBorderIcon sx={{ fontSize: 16 }} />,
      onClick: () => { if (currentAppId) { handleBizverScore(); } else { setUploadMode("new"); setOpenModal(true); } },
      active: bizverProcessing,
      color: "#64ffb4",
      featureCode: "MODEL_BUSINESS_VERIFY"
    },
    { label: "Fraud Risk Score",            icon: <ArrowUpwardIcon sx={{ fontSize: 16 }} />,  onClick: handleFraudRiskScore,     active: fraudProcessing,          color: "#ff6b6b", featureCode: "MODEL_FRAUD_RISK" },
    { label: "New Loan Score",              icon: <ShowChartIcon sx={{ fontSize: 16 }} />,    onClick: handleProbabilityScore,   active: probabilityProcessing,    color: "#64a0ff", featureCode: "MODEL_NEW_LOAN" },
   
    { label: "Loan Product Recommendation",icon: <ArrowUpwardIcon sx={{ fontSize: 16 }} />,  onClick: handleEligibilityScore,   active: eligibilityProcessing,    color: "#ffb432", featureCode: "MODEL_LOAN_PRODUCT" },
    { label: "Pre-Qualification Score",     icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   onClick: handlePrequalScore,       active: prequalProcessing,        color: "#00c896", featureCode: "MODEL_PRE_QUALIFICATION" },
    { label: "Early Warning Score",         icon: <ArrowUpwardIcon sx={{ fontSize: 16 }} />,  onClick: handleEarlyWarningScore,  active: earlyWarningProcessing,   color: "#ff8c00", featureCode: "MODEL_EARLY_WARNING" },
    { label: "Collection Score",            icon: <ShowChartIcon sx={{ fontSize: 16 }} />,    onClick: handleCollectionScore,    active: collectionProcessing,     color: "#64dcff", featureCode: "MODEL_COLLECTION_SCORE" },
    { label: "Unified Bureau Report",       icon: <StarBorderIcon sx={{ fontSize: 16 }} />,   onClick: handleBureauScore,        active: bureauProcessing,         color: "#b4dcff", featureCode: "MODEL_UNIFIED_BUREAU" },
  ];

  // 👇 ADDED: Dynamic filter to strip out models the user hasn't been approved for
  const visibleServices = services.filter(svc => {
    if (!svc.featureCode) return true;
    return allowedModels.includes(svc.featureCode);
  });

  // ── Utility: hex -> rgba ──
  const hexAlpha = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  // ── Shared Left Panel Content ──
  const LeftPanelContent = ({ insideDrawer = false }) => (
    <Box
      sx={{
        width: insideDrawer ? "clamp(11.25rem, 18vw, 20rem)" : (collapsed && !isMobile ? "3.5rem" : "clamp(11.25rem, 18vw, 20rem)"),
        bgcolor: "#002855",
        color: "white",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ overflowY: "auto", overflowX: "hidden", flex: 1, pt: 1.5, pb: 1 }}>
        <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center", px: (!insideDrawer && collapsed && !isMobile) ? 0 : 1.5 }}>
          <Box
            component="img"
            src={whiteLogo}
            alt="LendingX"
            sx={{
              width: (!insideDrawer && collapsed && !isMobile) ? "2rem" : "80%",
              borderRadius: 1,
              objectFit: "contain",
              transition: "width 0.3s ease",
            }}
          />
        </Box>

        {(insideDrawer || !collapsed || isMobile) && (
          <Typography fontWeight={700} fontSize={13} mb={2} sx={{ letterSpacing: 0.5, color: "rgba(255,255,255,0.85)", textAlign: "center", px: 1 }}>
            {loggedInUser || "Dashboard Analyzer"}
          </Typography>
        )}

        <Box sx={{ px: (!insideDrawer && collapsed && !isMobile) ? 0.5 : 1.5, mb: 0.75 }}>
          {(!insideDrawer && collapsed && !isMobile) ? (
            <Tooltip title="Add New Case" placement="right" arrow>
              <IconButton
                onClick={() => {
                  if (currentAppId) { window.location.reload(); }
                  else { setUploadMode("new"); setUploadFileType("pdf"); setOpenModal(true); }
                }}
                sx={{ bgcolor: "#00A88F", color: "white", width: "100%", borderRadius: 1, "&:hover": { bgcolor: "#00c4a8" } }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => {
                if (currentAppId) { window.location.reload(); }
                else { setUploadMode("new"); setUploadFileType("pdf"); setOpenModal(true); }
                if (insideDrawer) setLeftDrawerOpen(false);
              }}
              sx={{ bgcolor: "#00A88F", color: "white", borderRadius: 1, textTransform: "none", fontWeight: 600, fontSize: 13, "&:hover": { bgcolor: "#00c4a8" }, justifyContent: "center", px: 1.5 }}
            >
              Add New Case
            </Button>
          )}
        </Box>

        <Box sx={{ px: (!insideDrawer && collapsed && !isMobile) ? 0.5 : 1.5, mb: 2 }}>
          {(!insideDrawer && collapsed && !isMobile) ? (
            <Tooltip title={currentAppId ? "Add to Existing Case" : "No active case"} placement="right" arrow>
              <span style={{ display: "block" }}>
                <IconButton
                  onClick={() => { setUploadMode("existing"); setUploadFileType("pdf"); setOpenModal(true); }}
                  disabled={!currentAppId}
                  sx={{
                    width: "100%", borderRadius: 1,
                    bgcolor: !currentAppId ? "rgba(255,255,255,0.04)" : "rgba(0,168,143,0.15)",
                    color: !currentAppId ? "rgba(255,255,255,0.2)" : "#00A88F",
                    border: !currentAppId ? "1px solid transparent" : "1px solid rgba(0,168,143,0.4)",
                    "&:hover": { bgcolor: currentAppId ? "rgba(0,168,143,0.25)" : undefined },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.2)" },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => {
                setUploadMode("existing"); setUploadFileType("pdf"); setOpenModal(true);
                if (insideDrawer) setLeftDrawerOpen(false);
              }}
              disabled={!currentAppId}
              sx={{
                bgcolor: !currentAppId ? "rgba(255,255,255,0.06)" : "rgba(0,168,143,0.15)",
                color: !currentAppId ? "rgba(255,255,255,0.25)" : "#00A88F",
                borderRadius: 1, textTransform: "none", fontWeight: 600, fontSize: 13,
                border: !currentAppId ? "1px solid transparent" : "1px solid rgba(0,168,143,0.4)",
                "&:hover": { bgcolor: currentAppId ? "rgba(0,168,143,0.25)" : "rgba(255,255,255,0.06)" },
                "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.2)" },
                justifyContent: "center", px: 1.5,
              }}
            >
              Add to Existing Case
            </Button>
          )}
        </Box>

        {(insideDrawer || !collapsed || isMobile) && (
          <Typography fontSize={10} color="rgba(255,255,255,0.45)" mb={1} sx={{ letterSpacing: 1, px: 1.5, textAlign: "center" }}>
            ACTIVE DATA SOURCES
          </Typography>
        )}

        {(insideDrawer || !collapsed || isMobile) && displayFiles.map((f, i) => (
          <Box
            key={i}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              p: 1, mx: 1, mb: 0.75, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <Box
                onClick={() => toggleCheck(i)}
                sx={{
                  width: 16, height: 16, borderRadius: "50%",
                  bgcolor: f.checked ? "#00A88F" : "transparent",
                  border: f.checked ? "none" : "1.5px solid rgba(255,255,255,0.4)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: "white", flexShrink: 0, cursor: "pointer", transition: "all 0.2s",
                }}
              >
                {f.checked ? "✓" : ""}
              </Box>
              <Box sx={{ px: 0.5, py: 0.1, bgcolor: f.tag === "PDF" ? "#c0392b" : "#2980b9", borderRadius: 0.5, fontSize: 9, fontWeight: 700, color: "white", flexShrink: 0 }}>
                {f.tag}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize={11} fontWeight={600} color="white" lineHeight={1.2} noWrap>{f.name}</Typography>
                <Typography fontSize={10} color="rgba(255,255,255,0.5)">{f.meta}</Typography>
              </Box>
            </Box>
            <IconButton onClick={() => deleteFile(i)} size="small" sx={{ color: "rgba(255,255,255,0.35)", p: 0.2, flexShrink: 0, "&:hover": { color: "#ff6b6b" } }}>
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {!insideDrawer && collapsed && !isMobile && displayFiles.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, mt: 1 }}>
            {displayFiles.slice(0, 5).map((f, i) => (
              <Tooltip key={i} title={f.name} placement="right" arrow>
                <Box
                  sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: f.checked ? "#00A88F" : "rgba(255,255,255,0.25)", cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => toggleCheck(i)}
                />
              </Tooltip>
            ))}
            {displayFiles.length > 5 && (
              <Typography fontSize={9} color="rgba(255,255,255,0.4)">+{displayFiles.length - 5}</Typography>
            )}
          </Box>
        )}
      </Box>

      {(!insideDrawer && collapsed && !isMobile) ? (
        <Tooltip title="Logout" placement="right" arrow>
          <Box onClick={handleLogout} sx={{ display: "flex", justifyContent: "center", p: 1.5, cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}>
            <PowerSettingsNewIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.7)" }} />
          </Box>
        </Tooltip>
      ) : (
        <Box onClick={handleLogout} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, p: 1.5, cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}>
          <PowerSettingsNewIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.7)" }} />
          <Typography fontSize={13} fontWeight={500} sx={{ color: "rgba(255,255,255,0.7)" }}>Logout</Typography>
        </Box>
      )}
    </Box>
  );

  // ── Shared Right Panel Content ──
  const RightPanelContent = ({ insideDrawer = false }) => (
    <Box
      sx={{
        width: insideDrawer ? "clamp(11.25rem, 18vw, 20rem)" : "clamp(11.25rem, 18vw, 20rem)",
        bgcolor: "#002855",
        color: "white",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.5, pt: 2, pb: 1, flexShrink: 0 }}>
        <Typography fontSize={10} color="rgba(255,255,255,0.45)" sx={{ letterSpacing: 1 }}>INTELLIGENCE ENGINE</Typography>
        <Typography fontWeight={700} fontSize={15} mb={1}>Live Sandbox</Typography>
        <Typography fontSize={10} color="rgba(255,255,255,0.45)" mb={1} sx={{ letterSpacing: 1 }}>LENDINGX SERVICES</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 1.5, pb: 2 }}>
        {/* 👇 UPDATED: We map over visibleServices instead of all services! */}
        {visibleServices.map((svc, idx) => {
          const enabled = svc.alwaysEnabled || !!currentAppId;
          return (
            <Box
              key={idx}
              onClick={() => {
                if (enabled) {
                  svc.onClick();
                  if (insideDrawer) setRightDrawerOpen(false);
                }
              }}
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                p: "7px 10px", mb: 0.625,
                bgcolor: svc.active ? hexAlpha(svc.color, 0.18) : "rgba(255,255,255,0.06)",
                borderRadius: 1,
                cursor: enabled ? "pointer" : "not-allowed",
                opacity: enabled ? 1 : 0.45,
                border: svc.active ? `1px solid ${hexAlpha(svc.color, 0.5)}` : "1px solid transparent",
                transition: "all 0.2s",
                "&:hover": enabled ? { bgcolor: hexAlpha(svc.color, 0.15), border: `1px solid ${hexAlpha(svc.color, 0.4)}` } : {},
                minWidth: 0, whiteSpace: "normal", wordBreak: "break-word",
              }}
            >
              <Box sx={{ color: svc.active ? svc.color : "rgba(255,255,255,0.6)", flexShrink: 0, display: "flex", alignItems: "center", transition: "color 0.2s" }}>
                {svc.icon}
              </Box>
              <Typography
                fontSize={12} lineHeight={1.35}
                color={svc.active ? svc.color : "white"}
                fontWeight={svc.active ? 700 : 400}
                sx={{ transition: "all 0.2s", flex: 1, minWidth: 0 }}
              >
                {svc.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#002855",
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      {/* ── LEFT PANEL — Desktop/Tablet ── */}
      {!isMobile && (
        <Box
          sx={{
            width: collapsed ? "3.5rem" : "clamp(11.25rem, 18vw, 20rem)",
            flexShrink: 0,
            bgcolor: "#002855",
            color: "white",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            transition: "width 0.3s ease",
            overflow: "visible",
          }}
        >
          <LeftPanelContent insideDrawer={false} />
        </Box>
      )}

      {/* ── LEFT PANEL — Mobile Drawer ── */}
      {isMobile && (
        <SwipeableDrawer
          anchor="left"
          open={leftDrawerOpen}
          onClose={() => setLeftDrawerOpen(false)}
          onOpen={() => setLeftDrawerOpen(true)}
          PaperProps={{ sx: { bgcolor: "#002855", width: "clamp(11.25rem, 18vw, 20rem)" } }}
        >
          <LeftPanelContent insideDrawer={true} />
        </SwipeableDrawer>
      )}

      {/* ── CENTER PANEL ── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: "#f0f2f5",
          p: { xs: 1, sm: 1.5, md: 2 },
          overflow: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isMobile && (
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              mb: 1, flexShrink: 0,
            }}
          >
            <IconButton onClick={() => setLeftDrawerOpen(true)} sx={{ color: "#002855", p: 0.5 }}>
              <MenuIcon />
            </IconButton>
            <Typography fontWeight={700} fontSize={13} color="#002855">{loggedInUser || "Dashboard Analyzer"}</Typography>
            <IconButton onClick={() => setRightDrawerOpen(true)} sx={{ color: "#002855", p: 0.5 }}>
              <AppsIcon />
            </IconButton>
          </Box>
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            flexShrink: 0,
            background: "linear-gradient(135deg, #003366 0%, #005580 55%, #00867a 100%)",
            color: "white",
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ width: 36, height: 36, borderRadius: 1, overflow: "hidden", flexShrink: 0 }}>
            <Box component="img" src="/lendingx_logo.jpg" alt="LendingX" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={{ xs: 12, sm: 14 }}>LendingX Credit Intelligence System</Typography>
            <Typography fontSize={{ xs: 10, sm: 11 }} sx={{ opacity: 0.75 }}>Embed credit intelligence directly into your lending stack</Typography>
          </Box>
        </Paper>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 1,
            flexShrink: 0,
            "& .MuiTabs-indicator": { bgcolor: "#00A88F", height: 2 },
            "& .MuiTab-root": { textTransform: "none", fontSize: 13, minHeight: 40, color: "#666" },
            "& .Mui-selected": { color: "#00A88F !important", fontWeight: 600 },
          }}
        >
          <Tab icon={<DashboardIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Dashboard" />
          <Tab icon={<ShowChartIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="JSON" />
        </Tabs>

        {tab === 0 && (
          <Paper elevation={0} sx={{ borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, flex: 1, minHeight: 0 }}>
            {(activeMode === "loan_gemini" || activeMode === "loan_private") && dashboardImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Loan Approver Score Dashboard</Typography>
                  <IconButton component="a" href={dashboardImageUrl} download="final_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={dashboardImageUrl} alt="Loan Approver Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "fraud" && fraudImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Fraud Risk Score Dashboard</Typography>
                  <IconButton component="a" href={fraudImageUrl} download="fraud_risk_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={fraudImageUrl} alt="Fraud Risk Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "probability" && probabilityImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Probability Score Dashboard</Typography>
                  <IconButton component="a" href={probabilityImageUrl} download="new_loan_score.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={probabilityImageUrl} alt="Probability Score Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "identity" && identityImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Identity Confidence Score Dashboard</Typography>
                  <IconButton component="a" href={identityImageUrl} download="identity_confidence_score_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={identityImageUrl} alt="Identity Confidence Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "eligibility" && eligibilityImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Loan Eligibility Score Dashboard</Typography>
                  <IconButton component="a" href={eligibilityImageUrl} download="loan_eligibility_engine_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={eligibilityImageUrl} alt="Loan Eligibility Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "prequal" && prequalImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Pre-Qualification Score Dashboard</Typography>
                  <IconButton component="a" href={prequalImageUrl} download="pre_qualification_score_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={prequalImageUrl} alt="Pre-Qualification Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "earlywarning" && earlyWarningImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Early Warning Score Dashboard</Typography>
                  <IconButton component="a" href={earlyWarningImageUrl} download="early_warning_score_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={earlyWarningImageUrl} alt="Early Warning Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "collection" && collectionImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Collection Score Dashboard</Typography>
                  <IconButton component="a" href={collectionImageUrl} download="collection_score_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={collectionImageUrl} alt="Collection Score Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "bureau" && bureauImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Unified Bureau Score Dashboard</Typography>
                  <IconButton component="a" href={bureauImageUrl} download="Unified_Bureau_Report_dashboard.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={bureauImageUrl} alt="Unified Bureau Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "creditmemo" && creditmemoImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Credit Memo Score Dashboard</Typography>
                  <IconButton component="a" href={creditmemoImageUrl} download="credit_memo.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={creditmemoImageUrl} alt="Credit Memo Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : activeMode === "bizver" && bizverImageUrl ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Business Site Verification Dashboard</Typography>
                  <IconButton component="a" href={bizverImageUrl} download="business_site_verification.png" size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="img" src={bizverImageUrl} alt="Business Site Verification Dashboard" sx={{ width: "100%", borderRadius: 1.5, border: "1px solid #e0e0e0", maxHeight: { xs: 280, sm: 380, md: 460 }, objectFit: "contain" }} />
              </Box>
            ) : isAnyProcessing ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, flexDirection: "column", gap: 2 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid rgba(0,168,143,0.2)", borderTop: "3px solid #00A88F", animation: "spin 0.9s linear infinite", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
                <Typography fontSize={13} color="#888">{loadingMessage}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#bbb", fontSize: 14 }}>
                Upload files to see dashboard
              </Box>
            )}
          </Paper>
        )}

        {tab === 1 && (
          <Paper elevation={0} sx={{ borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {(activeMode === "loan_gemini" || activeMode === "loan_private") && jsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">extracted_financial_data.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/extracted_financial_data.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(jsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "fraud" && fraudJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">fraud_risk_data.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/fraud_risk_data.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(fraudJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "probability" && probabilityJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">new_loan_score.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/new_loan_score.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(probabilityJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "identity" && identityJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">identity_confidence_score.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/identity_confidence_score.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(identityJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "eligibility" && eligibilityJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">loan_eligibility_engine.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/loan_eligibility_engine.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(eligibilityJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "prequal" && prequalJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">pre_qualification_score.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/pre_qualification_score.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(prequalJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "earlywarning" && earlyWarningJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">early_warning_score.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/early_warning_score.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(earlyWarningJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "collection" && collectionJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">collection_score.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/collection_score.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(collectionJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "bureau" && bureauJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">Unified_Bureau_Report.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Output_JSON/Unified_Bureau_Report.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(bureauJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "creditmemo" && creditmemoJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">credit_memo.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Original_Docs/credit_memo.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(creditmemoJsonData, null, 2)}</Box>
              </Box>
            ) : activeMode === "bizver" && bizverJsonData ? (
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography fontWeight={700} fontSize={{ xs: 13, sm: 15 }} color="#111">business_verification.json</Typography>
                  <IconButton component="a" href={`${AZURE_BASE}/${currentAppId}/Original_Docs/business_verification.json`} download size="small" sx={{ color: "#00A88F" }}><DownloadIcon fontSize="small" /></IconButton>
                </Box>
                <Box component="pre" sx={{ fontSize: 12, color: "#333", bgcolor: "#f8f9fa", p: 2, borderRadius: 1.5, overflow: "auto", flex: 1, minHeight: 0, border: "1px solid #e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>{JSON.stringify(bizverJsonData, null, 2)}</Box>
              </Box>
            ) : isAnyProcessing ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, flexDirection: "column", gap: 2 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid rgba(0,168,143,0.2)", borderTop: "3px solid #00A88F", animation: "spin 0.9s linear infinite", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
                <Typography fontSize={13} color="#888">{loadingMessage}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#bbb", fontSize: 14 }}>
                Upload files to see JSON output
              </Box>
            )}
          </Paper>
        )}

        {showLoanOverlay && (
          <Box
            sx={{
              position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
              zIndex: 20, animation: "fadeSlideUp 0.25s ease",
              "@keyframes fadeSlideUp": { from: { opacity: 0, transform: "translateX(-50%) translateY(16px)" }, to: { opacity: 1, transform: "translateX(-50%) translateY(0)" } },
            }}
          >
            <Box sx={{ bgcolor: "#0d2b45", borderRadius: 2, px: { xs: 2, sm: 3 }, py: 1.5, display: "flex", alignItems: "center", gap: 2, border: "1px solid rgba(0,168,143,0.25)", minWidth: { xs: 220, sm: 280 } }}>
              <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: "2.5px solid rgba(0,168,143,0.25)", borderTop: "2.5px solid #00A88F", animation: "spin 0.9s linear infinite", flexShrink: 0, "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
              <Typography fontWeight={700} fontSize={{ xs: 12, sm: 14 }} color="white">{loadingMessage}</Typography>
              <IconButton onClick={() => setShowLoanOverlay(false)} size="small" sx={{ color: "rgba(255,255,255,0.4)", p: 0.2, ml: "auto", "&:hover": { color: "white" } }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── RIGHT PANEL — Desktop/Tablet ── */}
      {!isMobile && (
        <Box
          sx={{
            width: "clamp(11.25rem, 18vw, 20rem)",
            flexShrink: 0,
            bgcolor: "#002855",
            color: "white",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <RightPanelContent insideDrawer={false} />
        </Box>
      )}

      {/* ── RIGHT PANEL — Mobile Drawer ── */}
      {isMobile && (
        <SwipeableDrawer
          anchor="right"
          open={rightDrawerOpen}
          onClose={() => setRightDrawerOpen(false)}
          onOpen={() => setRightDrawerOpen(true)}
          PaperProps={{ sx: { bgcolor: "#002855", width: "clamp(11.25rem, 18vw, 20rem)" } }}
        >
          <RightPanelContent insideDrawer={true} />
        </SwipeableDrawer>
      )}

      {/* ── TOAST ── */}
      {toast && (
        <Box sx={{ position: "fixed", top: 20, right: 20, zIndex: 9999, bgcolor: "#00A88F", color: "white", px: 2.5, py: 1.2, borderRadius: 1.5, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
          {toast}
        </Box>
      )}

      {/* ── FILE LIMIT WARNING MODAL ── */}
      <Modal open={!!fileWarning} onClose={() => setFileWarning("")}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "90%", sm: 400 }, bgcolor: "#003a6b", color: "white", p: 3, borderRadius: 2, outline: "none", textAlign: "center" }}>
          <Box sx={{ fontSize: 36, mb: 1.5 }}>⚠️</Box>
          <Typography fontWeight={700} fontSize={16} mb={1}>{fileWarning.startsWith("Please upload PDF") ? "PDF Required" : "Action Required"}</Typography>
          <Typography fontSize={13} color="rgba(255,255,255,0.75)" mb={2.5} lineHeight={1.6}>{fileWarning}</Typography>
          <Button onClick={() => setFileWarning("")} sx={{ bgcolor: "#00A88F", color: "white", textTransform: "none", fontWeight: 600, px: 3, borderRadius: 1, "&:hover": { bgcolor: "#00c4a8" } }}>Got it</Button>
        </Box>
      </Modal>

      {/* ── UPLOAD MODAL ── */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "90%", sm: 420 }, bgcolor: "#003a6b", color: "white", p: { xs: 2, sm: 3 }, borderRadius: 2, outline: "none" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Typography fontWeight={700} fontSize={{ xs: 14, sm: 16 }}>
              {uploadFileType === "image" ? "Upload Images — Business Site Verification" : uploadMode === "new" ? "Create New Case" : "Add to Existing Case"}
            </Typography>
            <IconButton onClick={() => { setOpenModal(false); setUploadError(""); setUploadFileType("pdf"); }} size="small" sx={{ color: "white" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ border: "1.5px dashed rgba(0,168,143,0.6)", borderRadius: 2, py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, bgcolor: "rgba(0,168,143,0.05)" }}>
            <CloudUploadIcon sx={{ fontSize: 40, color: uploading ? "rgba(0,168,143,0.4)" : "#00A88F" }} />
            <Typography fontWeight={600} fontSize={14} color="white">{uploading ? "Uploading..." : "Drop files here to upload"}</Typography>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" textAlign="center" px={2}>
              {uploadMode === "existing" ? `PDF & image files · ${files.length} files uploaded so far` : "PDF & image files · No file limit"}
            </Typography>
            {uploadError && <Typography fontSize={11} color="#ff6b6b" fontWeight={600} textAlign="center" px={2}>{uploadError}</Typography>}
            {!uploading && (
              <Button
                component="label"
                sx={{ mt: 0.5, color: "#00A88F", textTransform: "none", fontSize: 13, fontWeight: 600, p: 0, "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}
              >
                Browse Files
                <input hidden type="file" multiple accept="application/pdf,image/*" onChange={uploadMode === "new" ? handleUpload : handleAddToExisting} />
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DashboardAnalyzer;